/**
 * 파일을 보내는 AJAX. Angular HttpClient 등 기존 라이브러리를 쓰지 않고 XMLHttpRequest로 직접 한다.
 * - 업로드 진행률(보낸 바이트 / 전체)을 알려 준다. 업로드가 끝나면 'waiting'(서버 처리 대기)으로 바뀐다
 * - AbortSignal로 취소한다. 시간 초과도 둔다
 * - 오류를 종류(abort, timeout, network, http)로 나눠 NewplaAiXhrError로 던진다. 서버가 보낸 메시지를 꺼낸다
 * - 쿠키 인증을 그대로 쓴다(같은 출처). 앱 인터셉터가 붙이는 시간대 헤더는 직접 붙인다
 * - 받은 JSON 외에는 아무것도 남기지 않는다. 파일은 FormData로만 넘긴다
 */

export type NewplaAiXhrPhase = 'uploading' | 'waiting';

export interface NewplaAiXhrProgress {
  phase: NewplaAiXhrPhase;
  loaded: number;
  total: number;
  /** 0~100. 전체 크기를 모르면 null */
  percent: number | null;
}

export interface NewplaAiXhrOptions {
  onProgress?: (p: NewplaAiXhrProgress) => void;
  signal?: AbortSignal;
  /** 응답까지 기다릴 최대 시간 (ms). 0이면 무한 */
  timeoutMs?: number;
}

export type NewplaAiXhrErrorKind = 'abort' | 'timeout' | 'network' | 'http';

export class NewplaAiXhrError extends Error {
  constructor(readonly kind: NewplaAiXhrErrorKind, message: string, readonly status = 0) {
    super(message);
    this.name = 'NewplaAiXhrError';
  }
}

/** 앱 인터셉터(NpxHttpAuthInterceptor)가 붙이는 시간대 헤더 이름 */
const TIMEZONE_HEADER = 'X-Client-TimeZone';

export function newplaAiXhrPost<T>(url: string, body: FormData, options: NewplaAiXhrOptions = {}): Promise<T> {
  const { onProgress, signal, timeoutMs = 0 } = options;

  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new NewplaAiXhrError('abort', '보내기를 취소했습니다.'));
      return;
    }

    const xhr = new XMLHttpRequest();
    let settled = false;
    const finish = (fn: () => void) => {
      if (!settled) {
        settled = true;
        signal?.removeEventListener('abort', onAbort);
        fn();
      }
    };
    const onAbort = () => xhr.abort();

    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.timeout = timeoutMs;
    xhr.responseType = 'text';
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      xhr.setRequestHeader(TIMEZONE_HEADER, timeZone);
    }
    // Content-Type은 붙이지 않는다. 브라우저가 multipart boundary를 넣어 정한다

    xhr.upload.onprogress = (e: ProgressEvent) => {
      onProgress?.({
        phase: 'uploading',
        loaded: e.loaded,
        total: e.lengthComputable ? e.total : 0,
        percent: e.lengthComputable && e.total > 0 ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : null,
      });
    };
    xhr.upload.onload = () => {
      onProgress?.({ phase: 'waiting', loaded: 0, total: 0, percent: 100 });
    };

    xhr.onload = () => finish(() => {
      const status = xhr.status;
      const parsed = parseJson(xhr.responseText);
      if (status >= 200 && status < 300) {
        resolve(parsed.ok ? (parsed.value as T) : (undefined as T));
        return;
      }
      reject(new NewplaAiXhrError('http', serverMessage(status, parsed.ok ? parsed.value : xhr.responseText), status));
    });
    xhr.onerror = () => finish(() => reject(new NewplaAiXhrError('network', '서버에 연결하지 못했습니다.')));
    xhr.ontimeout = () => finish(() => reject(new NewplaAiXhrError('timeout', '응답이 너무 오래 걸려 중단했습니다.')));
    xhr.onabort = () => finish(() => reject(new NewplaAiXhrError('abort', '보내기를 취소했습니다.')));

    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(body);
  });
}

function parseJson(text: string): { ok: true; value: unknown } | { ok: false } {
  if (!text) {
    return { ok: false };
  }
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

function serverMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const msg = b['message'] ?? b['msg'] ?? b['error'];
    if (typeof msg === 'string' && msg.trim()) {
      return msg;
    }
  }
  if (typeof body === 'string' && body.trim() && body.length < 300 && !body.trimStart().startsWith('<')) {
    return body;
  }
  switch (status) {
    case 401: return '로그인이 끝났습니다. 다시 로그인하세요.';
    case 403: return '권한이 없습니다.';
    case 413: return '파일이 너무 커서 서버가 받지 않았습니다.';
    case 504: return '접속 장애가 발생했습니다. 시스템 관리자에게 문의하세요.';
    default: return `요청을 처리하지 못했습니다. (${status})`;
  }
}
